import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ExternalLink, Printer, AlertCircle, Scale } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CurveBottom } from "../components/Decor";
import Panda from "../components/Panda";
import { ficoFactors, negativeInfo, scoreGoals, fundamentals, corrections, glossary, resources, blankWorksheets } from "../data/parents";

const H2 = ({ children }) => <h2 className="text-2xl sm:text-3xl font-bold text-[#0c4a6e] mb-4" style={{ fontFamily: "Literata, serif" }}>{children}</h2>;
const Card = ({ children, className = "" }) => <div className={`bg-white rounded-3xl shadow-md p-6 sm:p-8 ${className}`}>{children}</div>;

const Fundamentals = () => (
  <div className="space-y-10">
    <Card>
      <H2>Why credit literacy matters</H2>
      <div className="space-y-4 text-gray-700 leading-relaxed">{fundamentals.whyItMatters.map((p, i) => <p key={i}>{p}</p>)}</div>
    </Card>

    <Card>
      <H2>What a credit score measures</H2>
      <p className="text-gray-600 mb-6">Classic FICO scores are built from five weighted categories.</p>
      <div className="space-y-4" data-testid="fico-chart">
        {ficoFactors.map((f) => (
          <div key={f.factor}>
            <div className="flex justify-between text-sm font-bold text-[#0c4a6e] mb-1"><span>{f.factor}</span><span>{f.weight}%</span></div>
            <div className="h-6 bg-[#e9f9fd] rounded-full overflow-hidden"><div className="h-full rounded-full bar-grow" style={{ width: `${f.weight * 2.8}%`, background: f.color }} /></div>
            <p className="text-xs text-gray-500 mt-1">{f.reflects}</p>
          </div>
        ))}
      </div>
    </Card>

    <Card className="border-l-8 border-[#f7931e]">
      <h3 className="flex items-center gap-2 text-xl font-bold text-[#8a4b00]" style={{ fontFamily: "Literata, serif" }}><AlertCircle /> 2026 update: which scoring model is used</h3>
      <p className="text-gray-700 mt-3 leading-relaxed">{fundamentals.update2026}</p>
    </Card>

    <Card>
      <H2>Credit utilization: the corrected guidance</H2>
      <p className="text-gray-700 leading-relaxed">{fundamentals.utilization}</p>
      <div className="mt-6 grid grid-cols-3 gap-3 text-center">
        {[["< 10%", "Strongest scores", "#2e8b57"], ["< 30%", "Healthy target", "#63c79a"], ["30-50%", "Old advice - too high", "#e5356f"]].map(([a, b, c]) => (
          <div key={a} className="rounded-2xl p-4 text-white" style={{ background: c }}><p className="font-logo text-3xl">{a}</p><p className="text-xs font-bold mt-1">{b}</p></div>
        ))}
      </div>
    </Card>

    <Card>
      <H2>How long negative information stays on a report</H2>
      <Table>
        <TableHeader><TableRow><TableHead className="font-bold text-[#0c4a6e]">Item</TableHead><TableHead className="font-bold text-[#0c4a6e]">How long it reports</TableHead></TableRow></TableHeader>
        <TableBody>{negativeInfo.map((n) => <TableRow key={n.item}><TableCell className="font-semibold">{n.item}</TableCell><TableCell className="text-gray-700">{n.duration}</TableCell></TableRow>)}</TableBody>
      </Table>
      <p className="text-xs text-gray-500 mt-4">{fundamentals.fcraSource}</p>
    </Card>

    <Card>
      <H2>Debt consolidation vs. debt settlement</H2>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl p-5 bg-[#e8f8ef] border-2 border-[#63c79a]"><p className="font-bold text-[#0d4d33] mb-2">Debt consolidation loan</p><p className="text-gray-700 text-sm leading-relaxed">{fundamentals.consolidationVsSettlement.consolidation}</p></div>
        <div className="rounded-2xl p-5 bg-[#fff0f5] border-2 border-[#ee7aa6]"><p className="font-bold text-[#7a1f45] mb-2">Debt settlement / debt relief</p><p className="text-gray-700 text-sm leading-relaxed">{fundamentals.consolidationVsSettlement.settlement}</p></div>
      </div>
      <div className="mt-5 flex items-start gap-3 bg-[#e9f9fd] rounded-2xl p-4"><Scale className="text-[#1b8bc9] flex-shrink-0" /><p className="text-gray-700">When comparing offers, ask directly: <em className="font-semibold">"{fundamentals.consolidationVsSettlement.question}"</em></p></div>
    </Card>

    <Card>
      <H2>Credit-score goals for teens (directional)</H2>
      <div className="grid sm:grid-cols-4 gap-4">
        {scoreGoals.map((g) => (
          <div key={g.goal} className="rounded-2xl p-5 text-center border-t-8 bg-[#f7fdfe]" style={{ borderColor: g.color }}>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">{g.goal}</p>
            <p className="font-logo text-4xl text-[#0c4a6e] mt-1">{g.score}</p>
            <p className="text-sm text-gray-600 mt-2">{g.meaning}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-gray-500 mt-5">{fundamentals.benchmarksNote}</p>
    </Card>
  </div>
);

const Facts = () => (
  <Card>
    <H2>Corrected facts sheet</H2>
    <p className="text-gray-600 mb-6">A side-by-side record of substantive corrections made to the original manuscript.</p>
    <div className="space-y-4">
      {corrections.map((c) => (
        <div key={c.topic} className="rounded-2xl border border-[#e9f9fd] overflow-hidden">
          <p className="bg-[#e9f9fd] px-5 py-2 font-bold text-[#0c4a6e]">{c.topic}</p>
          <div className="grid md:grid-cols-2">
            <div className="p-5 border-b md:border-b-0 md:border-r border-[#e9f9fd]"><p className="text-xs font-bold uppercase tracking-widest text-[#e5356f] mb-1">Original text</p><p className="text-gray-600 text-sm italic">{c.original}</p></div>
            <div className="p-5 bg-[#f7fdfe]"><p className="text-xs font-bold uppercase tracking-widest text-[#2e8b57] mb-1">Correction</p><p className="text-gray-700 text-sm">{c.correction}</p></div>
          </div>
        </div>
      ))}
    </div>
  </Card>
);

const Glossary = () => (
  <Card>
    <H2>Glossary</H2>
    <Accordion type="single" collapsible className="w-full" data-testid="glossary-accordion">
      {glossary.map((g) => (
        <AccordionItem key={g.term} value={g.term}>
          <AccordionTrigger className="font-bold text-[#0c4a6e] text-left hover:text-[#e5356f]">{g.term}</AccordionTrigger>
          <AccordionContent className="text-gray-700 leading-relaxed">{g.def}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </Card>
);

const Resources = () => (
  <Card>
    <H2>Resources</H2>
    <div className="grid sm:grid-cols-2 gap-4">
      {resources.map((r) => (
        <div key={r.name} className="rounded-2xl border-2 border-[#e9f9fd] p-5 hover:border-[#1b8bc9] hover:-translate-y-0.5 transition-[border-color,transform]">
          {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="font-bold text-[#1b8bc9] inline-flex items-center gap-1 hover:underline">{r.name} <ExternalLink size={14} /></a> : <p className="font-bold text-[#0c4a6e]">{r.name}</p>}
          <p className="text-gray-600 text-sm mt-2">{r.desc}</p>
        </div>
      ))}
    </div>
  </Card>
);

const Worksheets = () => (
  <div className="space-y-8">
    <div className="flex items-center justify-between no-print">
      <p className="text-gray-600">Photocopy or print these pages for repeated family use.</p>
      <button onClick={() => window.print()} className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white inline-flex items-center gap-2" data-testid="print-all-worksheets"><Printer size={18} /> Print</button>
    </div>
    {blankWorksheets.map((w) => (
      <Card key={w.title} className="print-area">
        <h3 className="text-xl font-bold text-[#0c4a6e] mb-4" style={{ fontFamily: "Literata, serif" }}>{w.title}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-[#c9ecf5]">
            <thead><tr>{w.columns.map((c) => <th key={c} className="text-left px-3 py-2 bg-[#e9f9fd] border border-[#c9ecf5] font-bold text-[#0c4a6e]">{c}</th>)}</tr></thead>
            <tbody>{Array.from({ length: w.rows }).map((_, r) => <tr key={r}>{w.columns.map((c) => <td key={c} className="h-10 border border-[#c9ecf5]" />)}</tr>)}</tbody>
          </table>
        </div>
      </Card>
    ))}
  </div>
);

const SECTIONS = { fundamentals: Fundamentals, facts: Facts, glossary: Glossary, resources: Resources, worksheets: Worksheets };
const LABELS = { fundamentals: "Fundamentals", facts: "Corrected facts", glossary: "Glossary", resources: "Resources", worksheets: "Worksheets" };

const Parents = () => {
  const { section = "fundamentals" } = useParams();
  const navigate = useNavigate();
  const Section = SECTIONS[section] || Fundamentals;

  return (
    <div data-testid="parents-page">
      <section className="relative bg-[#7d5fb2] overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-6 pt-12 pb-24 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1 text-center md:text-left">
            <p className="text-[#f3c932] font-bold uppercase tracking-widest text-sm">Parents' area</p>
            <h1 className="text-white font-bold text-4xl sm:text-5xl mt-2" style={{ fontFamily: "Literata, serif" }}>Get comfortable with the basics first</h1>
            <p className="text-white/90 text-lg mt-4 max-w-xl">Before teaching a child, get fluent yourself. Everything here is adapted from Part One and the appendices of the book.</p>
          </div>
          <Panda size={160} accessory="glasses" scarf="#f3c932" item="card" />
        </div>
        <CurveBottom color="#f7fdfe" />
      </section>

      <section className="bg-[#f7fdfe] pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <Tabs value={SECTIONS[section] ? section : "fundamentals"} onValueChange={(v) => navigate(`/parents/${v}`)} className="-mt-6 relative z-10 no-print">
            <TabsList className="h-auto flex-wrap bg-white shadow-lg rounded-full p-1.5 gap-1" data-testid="parents-tabs">
              {Object.keys(SECTIONS).map((k) => (
                <TabsTrigger key={k} value={k} className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1b8bc9] data-[state=active]:text-white" data-testid={`parents-tab-${k}`}>{LABELS[k]}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="mt-10 step-in" key={section}><Section /></div>
          <p className="mt-10 text-center text-sm text-gray-500">Ready to teach? <Link to="/lessons" className="text-[#1b8bc9] font-bold hover:underline">Pick a lesson by age</Link>.</p>
        </div>
      </section>
    </div>
  );
};

export default Parents;
